import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  CheckMark,
  NewCloseIcon,
  RegWallContainer,
  TrackableLink,
} from '@surfline/quiver-react';
import { color } from '@surfline/quiver-themes';
import { getMeterRemaining, trackEvent } from '@surfline/web-common';

import config from '../../../../config';
import {
  RL_VERIFY_EMAIL_CLICKED,
  RL_CTA_CLICKED,
  RL_PAYWALL_DISPLAYED,
} from '../../../../common/constants';
import { SURF_CHECK_TREATMENT_VARIANT } from '../../../../common/treatments';
import { useMeterSelector } from '../../../../selectors/metering';
import { useUserDetailsSelector } from '../../../../selectors/user';

const MeterPremiumPaywall = () => {
  const verifyEmailLinkRef = useRef();
  const meterRemaining = useSelector(getMeterRemaining);
  const {
    treatmentVariant,
    limitedTimeOfferEligible,
    limitedTimeOfferDaysRemaining,
    limitedTimeOfferLink,
  } = useMeterSelector();
  const { isEmailVerified } = useUserDetailsSelector();
  useEffect(() => {
    trackEvent(RL_PAYWALL_DISPLAYED, {
      verified: isEmailVerified,
      pageName: window.location.href,
    });
  });
  const onClickCTA = () => {
    trackEvent(RL_CTA_CLICKED);
    setTimeout(() => {
      window.location.assign(`${limitedTimeOfferLink}`);
    }, 500);
  };
  const editProfileLink = `https://${config.surflineHost}/account/edit-profile`;
  const headerText =
    treatmentVariant === SURF_CHECK_TREATMENT_VARIANT ? 'Surf Checks left' : 'Views Remaining';
  const subHeaderText =
    treatmentVariant === SURF_CHECK_TREATMENT_VARIANT
      ? 'A Premium account will get you back in action with unlimited Surf Checks, ad-free cams, & more!'
      : 'A Premium account will get you back in action with unlimited access, ad-free cams, & more!';

  return meterRemaining !== null ? (
    <div className="sl-meter-premium-paywall__container">
      <RegWallContainer bgColor={color('dark-blue', 90)}>
        <div className="sl-meter-premium-paywall__fullscreen--content">
          <div className="sl-meter-premium-paywall__box-header">
            <div className="sl-meter-premium-paywall__count">0</div>
            <h5 className="sl-meter-premium-paywall__title">{headerText}</h5>
          </div>
          <p className="sl-meter-premium-paywall__fullscreen--subtitle">{subHeaderText}</p>
          <div className="sl-meter-premium-paywall__fullscreen--matrix">
            <div className="sl-meter-premium-paywall__fullscreen--matrix-feature__column">
              <h5 className="sl-meter-premium-paywall__fullscreen--matrix-header">FEATURES</h5>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row">Surf Checks</div>
              <div
                className="sl-meter-premium-paywall__fullscreen--matrix-row"
                style={{ marginTop: '5px' }}
              >
                Ad-Free Cams
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                Track & share your waves with Surfline Sessions
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                The best surf content from Premium Originals
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                Exclusive savings with Premium Perks
              </div>
            </div>
            <div className="sl-meter-premium-paywall__fullscreen--matrix-column">
              <h5 className="sl-meter-premium-paywall__fullscreen--matrix-header sl-meter-premium-paywall__fullscreen--matrix-premium">
                PREMIUM
              </h5>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-premium">
                Unlimited
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-premium">
                Unlimited
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                <CheckMark />
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                <CheckMark />
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                <CheckMark />
              </div>
            </div>
            <div className="sl-meter-premium-paywall__fullscreen--matrix-column sl-meter-premium-paywall__fullscreen--matrix-free-column">
              <h5 className="sl-meter-premium-paywall__fullscreen--matrix-header">FREE ACCOUNT</h5>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row">5 per week</div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row">
                <NewCloseIcon />
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                <NewCloseIcon />
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                <NewCloseIcon />
              </div>
              <div className="sl-meter-premium-paywall__fullscreen--matrix-row sl-meter-premium-paywall__fullscreen--matrix-row__large">
                <NewCloseIcon />
              </div>
            </div>
          </div>
          {isEmailVerified ? null : (
            <div className="sl-meter-premium-paywall__verify-email__text">
              <span>Not Ready to Upgrade? </span>
              <TrackableLink ref={verifyEmailLinkRef} eventName={RL_VERIFY_EMAIL_CLICKED}>
                <a ref={verifyEmailLinkRef} href={editProfileLink}>
                  Verify your email
                </a>
                to get new surf checks every monday.
              </TrackableLink>
            </div>
          )}

          <Button className="sl-meter-premium-paywall__actions" onClick={onClickCTA}>
            LEARN MORE
          </Button>
          <div className="sl-meter-premium-paywall__actions--mobile">
            <Button onClick={onClickCTA}>
              {limitedTimeOfferEligible ? (
                <>
                  <p>GET 1 MONTH FREE</p>
                  <span>{limitedTimeOfferDaysRemaining} DAYS LEFT</span>
                </>
              ) : (
                <p>LEARN MORE</p>
              )}
            </Button>
          </div>
          {limitedTimeOfferEligible ? (
            <span className="sl-meter-premium-paywall__promo__text">
              <b>{limitedTimeOfferDaysRemaining} days</b> left to claim your Free Month
            </span>
          ) : null}
        </div>
      </RegWallContainer>
    </div>
  ) : null;
};

export default MeterPremiumPaywall;
